import { useTranslation } from 'react-i18next';

import { Button, CheckCircleSolid, Flex, Link, Stack, Text } from '@bonusly/design-system';

import { ReactComponent as GoogleCalendar } from '../../assets/google-calendar-icon.svg';
import { ReactComponent as Outlook } from '../../assets/outlook-icon.svg';

interface CalendarEventDetailsProps {
	externalCalendarLink: string | null;
	externalCalendarProvider: string | null;
	frequency: string;
	nextMeetingDate?: Date;
	startDateTime: Date;
	endDateTime: Date;
	title: string | null;
	occurrenceInMonth: string | null;
	onUnlinkEvent?: () => void;
	scheduleDayOfWeek: string;
}

export function CalendarEventDetails({ externalCalendarLink, externalCalendarProvider, onUnlinkEvent }: CalendarEventDetailsProps) {
	const { t } = useTranslation('one_on_ones');

	let calendarIcon;
	let providerName = '';
	if (externalCalendarProvider === 'google-calendar') {
		calendarIcon = <GoogleCalendar />;
		providerName = 'Google Calendar';
	} else if (externalCalendarProvider === 'outlook') {
		calendarIcon = <Outlook />;
		providerName = 'Outlook';
	} else {
		calendarIcon = <CheckCircleSolid color="text.interactive" size="sm" />;
		providerName = 'external calendar';
	}

	return (
		<Stack rounded="sm" borderColor="border.default" borderWidth="1px" p={3} gap={3} bgColor="primary.bgAlpha">
			<Stack gap={0} mx={-3} mb={-3}>
				<Flex alignItems="center" gap={2} justifyContent="space-between" px={3} py={1}>
					{calendarIcon}
					<Text size="md" flex="1" fontWeight="semibold">
						{t('meeting.modal.calendar.linked_to_provider', { provider: providerName })}
					</Text>
					{onUnlinkEvent && (
						<Button colorScheme="secondary" size="xs" variant="alpha" onClick={onUnlinkEvent}>
							{t('meeting.modal.calendar.remove_link')}
						</Button>
					)}
				</Flex>
				<Text size="sm" color="text.default" px={3} pb={3}>
					To change the meeting time or cancel this 1:1, make updates to your{' '}
					<Link href={externalCalendarLink || undefined} isExternal textDecoration="underline">
						{providerName} event
					</Link>
					.
				</Text>
			</Stack>
		</Stack>
	);
}
